import { Flex } from '@hover/blueprint';
import { iArrowLeft, iSearch } from '@hover/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { CardHeader } from 'src/modules/designStudio/components/DesignMenu/CardHeader';
import { FilterTab } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults/FilterTab';
import { ProviderVariations } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults/ProviderVariations';
import type {
  ProvidersSummary,
  VariationsGroupedByProvider,
} from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults/utils';
import { extractProviderSummariesWithUniqueProducts } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults/utils';
import {
  TestIds,
  TestPageIds,
} from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import { useProductRankings } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/useProductRankings';
import { useTrackProductSelectionEvent } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/useTrackProductSelectionEvent';
import { ScrollMenu } from 'src/modules/designStudio/components/DesignMenu/ScrollMenu';
import type {
  Product,
  ProductCategory,
  ProductFilter,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';

const MAX_NUM_VARIATIONS_IN_GROUP = 6;

enum ActiveView {
  VIEW_MULTIPLE_MANUFACTURERS,
  VIEW_ONE_MANUFACTURER,
}

export type FilteredProductsParams = {
  category: ProductCategory;
  filteredProducts: Product[];
  selectedVariation?: Variation;
  onBack: () => void;
  onSearchByName: () => void;
  onApplyVariation: (variation: Variation) => void;
  onViewVariationDetails: (variation: Variation) => void;
  onChangeFilterColor: () => void;
  onChangeFilter: () => void;
  title: string;
  filter: ProductFilter;
};

/**
 * @description This view allows the user to view all the products that satisfy their filter. From this screen
 * the user can do the following:
 * - view list of products that satisfy filter
 * - open up filter screen to change their filter options
 * - search for products by their name
 */
export const FilteredSearchResults = ({
  category,
  filter,
  selectedVariation,
  filteredProducts,
  onBack,
  onSearchByName,
  onApplyVariation,
  onViewVariationDetails,
  onChangeFilterColor,
  onChangeFilter,
  title,
}: FilteredProductsParams) => {
  const [activeView, setActiveView] = useState<ActiveView>(
    ActiveView.VIEW_MULTIPLE_MANUFACTURERS,
  );

  const parentRef = useRef(null);

  const { trackRealProductSelectionEvent } = useTrackProductSelectionEvent({
    category,
  });

  const { rankProviderSummaries } = useProductRankings();

  const providerSummaries = useMemo(
    () =>
      rankProviderSummaries(
        filter.productType,
        extractProviderSummariesWithUniqueProducts(filteredProducts),
      ),
    [filter, filteredProducts, rankProviderSummaries],
  );

  const [selectedSummary, setSelectedSummary] = useState<
    VariationsGroupedByProvider | undefined
  >(undefined);

  const totalNumVariations = providerSummaries.reduce(
    (acc, summary) => acc + summary.allVariations.length,
    0,
  );

  useEffect(() => {
    if (parentRef && parentRef.current) {
      (parentRef.current as HTMLElement).scrollIntoView(true);
    }
  }, [activeView]);

  /**
   * filteredProducts is a flat list of Product,
   * if activeView = VIEW_MULTIPLE_MANUFACTURERS
   *    we want to group products by providers, then group all variations by unique colors
   */

  const actionsByView: Record<
    ActiveView,
    Record<string, (...args: any) => void>
  > = {
    [ActiveView.VIEW_MULTIPLE_MANUFACTURERS]: {
      onViewAllVariationsInOneProvider: (summary: ProvidersSummary) => {
        trackRealProductSelectionEvent({
          track_id: `product-selection-${category}-view-all-click-manufacturer`,
        });
        setSelectedSummary({
          provider: summary.provider,
          variations: summary.allVariations,
        });
        setActiveView(ActiveView.VIEW_ONE_MANUFACTURER);
      },
    },
    [ActiveView.VIEW_ONE_MANUFACTURER]: {
      onBack: () => {
        trackRealProductSelectionEvent({
          track_id: `product-selection-${category}-hide-options-click-manufacturer`,
        });
        setActiveView(ActiveView.VIEW_MULTIPLE_MANUFACTURERS);
      },
    },
  };

  const viewByState: Record<ActiveView, React.ReactElement> = {
    [ActiveView.VIEW_MULTIPLE_MANUFACTURERS]: (
      <ScrollMenu.Menu data-testid={TestPageIds.PRODUCT_RESULTS}>
        <ScrollMenu.Header>
          <CardHeader
            headerText={title}
            leftActionHandler={onBack}
            leftIcon={iArrowLeft}
            leftIconId={TestIds.BACK_BUTTON}
            m={0}
            rightActionHandler={onSearchByName}
            rightIcon={iSearch}
            rightIconId={TestIds.SEARCH_BUTTON}
          />
          <FilterTab
            filter={filter}
            numResults={totalNumVariations}
            onColorButtonClicked={onChangeFilterColor}
            onFilterButtonClicked={onChangeFilter}
          />
        </ScrollMenu.Header>
        <ScrollMenu.Body>
          <Flex direction="column" gap={600}>
            {providerSummaries.map(providerSummary => (
              <ProviderVariations
                key={`provider-${providerSummary.provider.uuid}`}
                onApplyVariation={onApplyVariation}
                onShowViewAll={() => {
                  actionsByView[
                    ActiveView.VIEW_MULTIPLE_MANUFACTURERS
                  ].onViewAllVariationsInOneProvider(providerSummary);
                }}
                onViewVariationDetails={onViewVariationDetails}
                selectedVariation={selectedVariation}
                showViewAll
                variationsGroupedByProvider={{
                  provider: providerSummary.provider,
                  variations: providerSummary.uniqueColoredVariations.filter(
                    (_, index) => index < MAX_NUM_VARIATIONS_IN_GROUP,
                  ),
                }}
              />
            ))}
          </Flex>
        </ScrollMenu.Body>
      </ScrollMenu.Menu>
    ),
    [ActiveView.VIEW_ONE_MANUFACTURER]: (
      <ScrollMenu.Menu
        data-testid={TestPageIds.PRODUCT_RESULTS_ONE_MANUFACTURER}
      >
        <ScrollMenu.Header>
          <CardHeader
            headerText={title}
            leftActionHandler={
              actionsByView[ActiveView.VIEW_ONE_MANUFACTURER].onBack
            }
            leftIcon={iArrowLeft}
            leftIconId={TestIds.BACK_BUTTON}
            m={0}
          />
        </ScrollMenu.Header>
        <ScrollMenu.Body>
          <ProviderVariations
            onApplyVariation={onApplyVariation}
            onViewVariationDetails={onViewVariationDetails}
            selectedVariation={selectedVariation}
            showViewAll={false}
            variationsGroupedByProvider={selectedSummary!}
          />
        </ScrollMenu.Body>
      </ScrollMenu.Menu>
    ),
  };

  return viewByState[activeView];
};
